import { useId, useState } from 'react';

import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ClockIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { META_ERROR_NOTICE_NAMESPACE } from 'src/utils/metaErrorNotice';

import { tMetaErrorNoticeProps, tMetaErrorNoticeVariant } from './MetaErrorNotice.types';

import './MetaErrorNotice.styles.scss';

const VARIANT_ICON: Record<tMetaErrorNoticeVariant, typeof ExclamationCircleIcon> = {
  error: ExclamationCircleIcon,
  info: InformationCircleIcon,
  pending: ClockIcon,
  ours: NoSymbolIcon,
};

/**
 * INT-1501: the notice inside a message bubble that says why WhatsApp did not
 * deliver it, or that Meta is reviewing it, and what to do next. Replaces the
 * pulsing red icon whose explanation only showed on hover.
 *
 * Copy arrives translated (the INT-1502 catalog resolves it per code); the
 * component only owns its fixed labels.
 *
 * INT-1500 (R1): title, text and the action row are always in sight. The
 * action and the "Cómo resolverlo" toggle share a row; steps, the note,
 * Meta's "Cómo funciona" link and its technical detail sit behind the toggle,
 * collapsed by default.
 * Only `visibleNote` (the D5 utility-template warning) stays next to its
 * button.
 */
export default function MetaErrorNotice(props: tMetaErrorNoticeProps) {
  const { t } = useTranslation(META_ERROR_NOTICE_NAMESPACE);
  const [isExpanded, setIsExpanded] = useState(false);
  const howToId = useId();

  const variant = props.variant ?? 'error';
  const Icon = VARIANT_ICON[variant];
  const hasSteps = Boolean(props.steps && props.steps.length > 0);
  const hasHowTo =
    hasSteps ||
    Boolean(props.note) ||
    Boolean(props.learnMore) ||
    Boolean(props.technicalDetail);
  const codeLabel = props.code ? t('code', { code: props.code }) : undefined;

  return (
    <div
      role="status"
      className={classNames('MetaErrorNotice', `MetaErrorNotice--${variant}`)}
      data-testid="meta-error-notice"
    >
      <div className="MetaErrorNotice__Header">
        <p className="MetaErrorNotice__Title">
          <Icon className="MetaErrorNotice__TitleIcon" aria-hidden="true" />
          <span>{props.title}</span>
        </p>
        {codeLabel && props.codeHref && (
          <a
            className="MetaErrorNotice__Code MetaErrorNotice__Code--link"
            href={props.codeHref}
            target="_blank"
            rel="noopener"
            aria-label={t('codeLinkLabel', { code: props.code })}
          >
            {codeLabel}
            <ArrowTopRightOnSquareIcon className="MetaErrorNotice__CodeIcon" aria-hidden="true" />
          </a>
        )}
        {codeLabel && !props.codeHref && (
          <span className="MetaErrorNotice__Code">{codeLabel}</span>
        )}
      </div>

      <p className="MetaErrorNotice__Text">{props.text}</p>

      {(props.action || props.secondaryAction || hasHowTo) && (
        <div className="MetaErrorNotice__Actions">
          {props.action?.type === 'internal' && (
            <button
              type="button"
              className="ls-btn ls-btn-sm ls-btn-tertiary"
              onClick={props.action.onClick}
              disabled={props.action.disabled}
            >
              {props.action.label}
            </button>
          )}
          {props.action?.type === 'external' && (
            <a
              className="ls-btn ls-btn-sm ls-btn-tertiary"
              href={props.action.href}
              target="_blank"
              rel="noopener"
              aria-label={t('opensInNewTab', { label: props.action.label })}
            >
              {props.action.label}
              <ArrowTopRightOnSquareIcon
                className="MetaErrorNotice__ActionIcon"
                aria-hidden="true"
              />
            </a>
          )}
          {props.secondaryAction && (
            <button
              type="button"
              className="ls-btn ls-btn-sm ls-btn-ghost"
              onClick={props.secondaryAction.onClick}
              disabled={props.secondaryAction.disabled}
            >
              {props.secondaryAction.label}
            </button>
          )}
          {hasHowTo && (
            <button
              type="button"
              className="MetaErrorNotice__Toggle"
              aria-expanded={isExpanded}
              aria-controls={howToId}
              onClick={() => {
                if (!isExpanded) props.onHowToOpen?.();
                setIsExpanded(!isExpanded);
              }}
            >
              <span>{t('howToFix')}</span>
              <ChevronDownIcon
                className={classNames('MetaErrorNotice__ToggleIcon', {
                  'MetaErrorNotice__ToggleIcon--open': isExpanded,
                })}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      )}

      {props.visibleNote && (
        <p className="MetaErrorNotice__Note MetaErrorNotice__VisibleNote">
          <InformationCircleIcon className="MetaErrorNotice__NoteIcon" aria-hidden="true" />
          <span>{props.visibleNote}</span>
        </p>
      )}

      {hasHowTo && isExpanded && (
        <div className="MetaErrorNotice__HowTo" id={howToId}>
          <p className="MetaErrorNotice__HowToTitle">{t('howToFix')}</p>
          {hasSteps && (
            <ol className="MetaErrorNotice__Steps">
              {props.steps?.map((step, index) => (
                // Static copy with no identity of its own; the order is the key
                <li key={index} className="MetaErrorNotice__Step">
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          )}
          {props.note && (
            <p className="MetaErrorNotice__Note">
              <InformationCircleIcon className="MetaErrorNotice__NoteIcon" aria-hidden="true" />
              <span>{props.note}</span>
            </p>
          )}
          {props.learnMore && (
            <a
              className="MetaErrorNotice__LearnMore"
              href={props.learnMore.href}
              target="_blank"
              rel="noopener"
              aria-label={t('opensInNewTab', { label: props.learnMore.label })}
            >
              {props.learnMore.label}
              <ArrowTopRightOnSquareIcon
                className="MetaErrorNotice__CodeIcon"
                aria-hidden="true"
              />
            </a>
          )}
          {props.technicalDetail && (
            <div className="MetaErrorNotice__Detail">
              <p className="MetaErrorNotice__DetailTitle">{t('technicalDetail')}</p>
              <p className="MetaErrorNotice__DetailText">{props.technicalDetail}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
